/**
 * Spec 053 Section F (Phase 3), Flow F7 — provider settlements: Garagat paying the recovery
 * providers for the pickup/return trips they actually drove. Served by **merchant-settlement**
 * under `/api/admin/provider-settlements/**` (gateway route `settlement-admin-provider-settlements`,
 * screen `settlements`).
 *
 * Shapes mirror `ProviderSettlementResponse` / `ProviderSettlementDetailResponse` /
 * `ProviderTripResponse` field for field.
 *
 * This is Garagat ↔ provider only: no partner and no customer ever sees any of it, and nothing here
 * touches the customer's delivery fee, which was fixed at booking and is independent of who drove
 * (AC-41b).
 *
 * The one rule this file exists to keep honest: **a cost of `null` is not a cost of zero.** Every
 * amount below is nullable, because a provider may have no cost in the registry at all (AC-43) —
 * the settlement then lists the trips and the period, and the invoiced amount is typed in when the
 * payment is recorded. Rendering that blank as `0.00` would read as "the trips were free".
 */

/** DRAFT → PAID, or DRAFT → VOID (which returns every trip to the pool, BR-28). */
export type ProviderSettlementStatus = 'DRAFT' | 'PAID' | 'VOID';

export const PROVIDER_SETTLEMENT_STATUSES: ProviderSettlementStatus[] = ['DRAFT', 'PAID', 'VOID'];

/**
 * The orders-service `PickupDeliveryType` name, carried verbatim on `TripCompletedEvent.leg`.
 * Deliberately **not** the `PICKUP | DROPOFF` spelling the WhatsApp driver template uses — the two
 * are independent wire contracts.
 */
export type TripLeg = 'PICKUP' | 'RETURN_DELIVERY';

/** `GET /api/admin/provider-settlements` row (`ProviderSettlementResponse`). */
export interface ProviderSettlement {
  id: string;
  settlementNumber: string;
  providerId: string;
  providerName: string;
  /** `CONTRACTOR | IN_HOUSE`; widened so an unknown value renders rather than crashing. */
  providerType: 'CONTRACTOR' | 'IN_HOUSE' | string;
  providerTrn: string | null;
  providerVatRegistered: boolean | null;
  status: ProviderSettlementStatus | string;
  /** First and last trip date, `YYYY-MM-DD`. */
  periodStart: string | null;
  periodEnd: string | null;
  tripCount: number;
  /** Σ ex-VAT trip costs — **null when `costConfigured` is false** (AC-43), never 0 in that case. */
  amount: number | null;
  vatTotal: number | null;
  /** False when at least one trip has no configured cost, which is why `amount` is blank. */
  costConfigured: boolean;
  /** AC-43's label ("N trips, cost not configured"), decided on the server; null when the amount is real. */
  amountNote: string | null;
  paymentReference: string | null;
  /** What was actually paid, typed at Record payment. Null until PAID. */
  paymentAmount: number | null;
  paymentDate: string | null;
  paidAt: string | null;
  voidReason: string | null;
  createdAt: string;
  /**
   * The input VAT this payment claimed — decided at payment from the provider's VAT registration
   * then (amount × 5/105, or 0). Null until paid, and null on settlements paid before 2026-09-27
   * (their VAT is in the ledger only).
   */
  vatAmount: number | null;
  /** Whether the provider's invoice is on file — "Invoice missing" in the list when false. */
  invoiceUploaded: boolean;
}

/** One completed Garagat leg on a settlement (AC-42: order number, leg, date, driver, cost). */
export interface ProviderTrip {
  /** `pickup_deliveries.id` — stable per (order, leg) and unchanged by reassignment. */
  tripId: string;
  orderId: string;
  orderNumber: string | null;
  leg: TripLeg | string;
  providerId: string | null;
  providerName: string | null;
  providerType: string | null;
  /** #634 — the provider's TRN as frozen on the trip; null for an unregistered or in-house provider. */
  providerTrn: string | null;
  providerVatRegistered: boolean | null;
  /** The cost snapshotted at assignment, ex-VAT. Null = never configured (AC-43). */
  costPerTrip: number | null;
  /** Free text per trip; there are no driver profiles (BR-27). */
  driverName: string | null;
  /** When the leg completed (ISO instant). */
  completedAt: string | null;
}

/** `GET /api/admin/provider-settlements/{id}` (`ProviderSettlementDetailResponse`). */
export interface ProviderSettlementDetail {
  settlement: ProviderSettlement;
  trips: ProviderTrip[];
  /** Presigned link to a payment proof (before 2026-09-27 this held "invoice or payment proof"). */
  paymentProofUrl: string | null;
  /** Presigned link to the provider's invoice; null until one is uploaded. */
  invoiceUrl: string | null;
}

/**
 * #634 — `GET /api/admin/provider-settlements/unsettled-trips?providerId=`
 * (`UnsettledProviderTripsResponse`): the provider's completed trips that are on no settlement yet —
 * exactly the pool **Prepare settlement** would collect, oldest first. A trip on a DRAFT or PAID
 * settlement is not in it; a void puts its trips back.
 *
 * `total` / `vatTotal` follow AC-43 like a settlement's `amount`: **null** as soon as one trip has
 * no configured cost (then `costConfigured` is false and `amountNote` carries the server's
 * "N trips, cost not configured"). An empty pool is `tripCount` 0 with totals 0.00.
 */
export interface UnsettledProviderTrips {
  providerId: string;
  /** The period asked for, `YYYY-MM-DD` Dubai dates; null = open on that side. */
  from: string | null;
  to: string | null;
  /** Distinct orders among the listed trips (an order with both legs counts once). */
  orderCount: number;
  /** Unsettled trips that completed BEFORE `from` — shown so an unpaid earlier month is not forgotten. */
  olderTripCount: number;
  tripCount: number;
  /** Σ ex-VAT frozen trip costs; null when any trip has no cost. */
  total: number | null;
  /** VAT on the VAT-registered trips only; null when `total` is. */
  vatTotal: number | null;
  costConfigured: boolean;
  amountNote: string | null;
  trips: ProviderTrip[];
}

/**
 * #634 — `GET /api/admin/provider-settlements/unsettled-providers` (`UnsettledProviderResponse`): a
 * provider that still has trips on no settlement, and how many. Read from the trips, so a provider
 * made inactive or retired in the registry while still owed is listed too. `providerName` is the
 * snapshot the trips carry.
 */
export interface UnsettledProvider {
  providerId: string;
  providerName: string | null;
  tripCount: number;
}

/** A period of trips: `from` omitted = every unsettled trip up to `to`. Dubai dates, inclusive. */
export interface TripPeriod {
  from?: string;
  to?: string;
}

/**
 * `POST …/{id}/pay` (a draft prepared before 2026-09-27) — only the amount is mandatory now.
 * `providerVatRegistered` decides the VAT claimed; omitted, the server uses the trips' snapshot.
 */
export interface RecordProviderPaymentInput {
  amount: number;
  reference?: string;
  invoice?: File | null;
  providerVatRegistered?: boolean;
  /** Optional; the server defaults to today in Dubai. */
  paymentDate?: string;
}

/** `POST …/pay-period` — mark one provider's unsettled trips in a period as paid, in one step. */
export interface PayPeriodInput {
  providerId: string;
  from?: string;
  to: string;
  /** The trip count the admin was looking at; the server answers 409 if it no longer matches. */
  tripCount: number;
  amount: number;
  /** Decides the VAT claimed: amount × 5/105 when true, 0 when false. */
  providerVatRegistered: boolean;
  providerTrn?: string | null;
  paymentDate?: string;
  reference?: string;
  invoice?: File | null;
}

/** UAE standard rate. Display only — the server computes and stores the figure it posts. */
export const VAT_RATE = 0.05;

/** The VAT inside a VAT-inclusive amount, rounded to fils: amount × 5/105. */
export function vatIncluded(amount: number): number {
  return Math.round(((amount * VAT_RATE) / (1 + VAT_RATE)) * 100) / 100;
}

/** Today's date in Dubai as `YYYY-MM-DD` — the calendar every finance period uses (BR-20). */
export function dubaiToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai', year: 'numeric', month: '2-digit', day: '2-digit' })
    .format(now);
}

/** `YYYY-MM` of the Dubai month before `today` (a `YYYY-MM-DD`). */
export function previousMonth(today: string): string {
  const [y, m] = today.split('-').map(Number);
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/** First and last day of a `YYYY-MM` month, inclusive. */
export function monthRange(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

const MONTH_LABEL = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** "September 2026" for a `YYYY-MM-DD` or `YYYY-MM`. */
export function monthLabel(date: string): string {
  const [y, m] = date.split('-').map(Number);
  return MONTH_LABEL.format(new Date(Date.UTC(y, m - 1, 1)));
}

/** How many trips on a loaded detail have no configured cost — the reason `amount` is blank. */
export function tripsWithoutCost(trips: ProviderTrip[]): number {
  return trips.filter((t) => t.costPerTrip === null || t.costPerTrip === undefined).length;
}

/**
 * The recoverable VAT to show beside the amount (#577), or `null` when there is none to show: a
 * provider with no TRN cannot issue a tax invoice, so nothing was accrued to input VAT for it.
 * Blank `amount` means blank VAT too — there is nothing to charge VAT on yet.
 */
export function settlementVat(s: ProviderSettlement): number | null {
  if (s.amount === null || s.amount === undefined) return null;
  if (s.providerVatRegistered !== true) return null;
  return s.vatTotal ?? 0;
}

/**
 * What the provider will actually invoice, and therefore what the record-payment form should offer
 * (#577).
 *
 * `amount` is the sum of **ex-VAT** trip costs, but the ledger accrued cost **plus** recoverable
 * VAT to the provider payable. Offering the ex-VAT figure to a VAT-registered contractor under-paid
 * them by the VAT and stranded that VAT in the payable account permanently, and nothing on screen
 * said which basis the number was on. So: cost + VAT for a registered provider, cost alone for one
 * that is not, and `null` (type it in) when no cost is configured at all — AC-43.
 *
 * It stays a pre-fill either way. The provider's own invoice remains authoritative and the admin
 * can change it; the backend posts whatever is recorded.
 */
export function invoiceableAmount(s: ProviderSettlement): number | null {
  if (s.amount === null || s.amount === undefined) return null;
  return s.amount + (settlementVat(s) ?? 0);
}

/** `PICKUP` → "Pickup", `RETURN_DELIVERY` → "Return"; anything else is shown as sent. */
export function legLabel(leg: TripLeg | string): string {
  if (leg === 'PICKUP') return 'Pickup';
  if (leg === 'RETURN_DELIVERY') return 'Return';
  return String(leg).replace(/_/g, ' ');
}

const PERIOD = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const TRIP_DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Dubai' });

/** "3 Sept – 14 Sept" for a settlement period; an em dash when there are no dates to show. */
export function formatPeriod(start: string | null, end: string | null): string {
  if (!start || !end) return '—';
  const s = new Date(`${start}T00:00:00Z`);
  const e = new Date(`${end}T00:00:00Z`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return `${start} → ${end}`;
  return start === end ? PERIOD.format(s) : `${PERIOD.format(s)} – ${PERIOD.format(e)}`;
}

/** Trip completion stamp in Dubai, the calendar the whole finance module reports in (BR-20). */
export function formatTripDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : TRIP_DATE.format(d);
}
